import { useQuery } from "@tanstack/react-query";
import { to } from "await-to-js";
import { useContext } from "react";
import { ModalsContext } from "../../context/modals.context";
import { mapSelectOptions } from "../../utils/map4select";
import { mfinKeys } from "./ministry.keys";
import { ministryService } from "./ministry.service";
import {
  FilterSelectOption,
  MfinExciseLicenceRequestListPayload,
  MfinExciseLicenceRequestListResponse,
  MfinExciseStampRequestListPayload,
  MfinExciseStampRequestListResponse,
  MfinExciseWarehouseListPayload,
  MfinExciseWarehouseListResponse,
  MfinListaEvidentiranihAkciznihMarkicaPayload,
  MfinListaEvidentiranihAkciznihMarkicaResponse,
} from "./ministry.types";
import { sanitizeMfinEvidentiraneMarkiceFilters } from "./ministry.usedStamps.constants";

/**
 * Hook used to fetch data for mfin excise stamp request list
 * @param filters Filters applied to table data
 * @param enabled If true API call is initiated immediately
 * @returns Filter dropdown values that can be applied to data and data after/before data
 */
export function useMfinExciseStampRequestList({
  filters,
  enabled,
}: {
  filters: MfinExciseStampRequestListPayload;
  enabled?: boolean;
}) {
  return useQuery({
    ...mfinKeys.exciseStampRequestList({ filters }),
    queryFn: () => ministryService.postMfSviZahteviZaMarkicu(filters),
    select: (response): MfinExciseStampRequestListResponse => ({
      exciseStampRequestList: response.data?.mfAllZahtevi ?? [],
      productTypes: mapSelectOptions<FilterSelectOption>({
        labelAccessor: "name",
        valueAccessor: "id",
        sourceArray: response.data?.vrsteFilters,
      }),
      statuses: mapSelectOptions<FilterSelectOption>({
        labelAccessor: "name",
        valueAccessor: "id",
        sourceArray: response.data?.filters,
      }),
    }),
    retry: false,
    enabled,
  });
}

/**
 * Hook used to fetch data for mfin excise licence request list
 * @param filters Filters applied to table data
 * @param enabled If true API call is initiated immediately
 * @returns Filter dropdown values that can be applied to data and data after/before data
 */
export function useMfinExciseLicenceRequestList({
  filters,
  enabled,
}: {
  filters: MfinExciseLicenceRequestListPayload;
  enabled?: boolean;
}) {
  return useQuery({
    ...mfinKeys.exciseLicenceRequestList({ filters }),
    queryFn: () => ministryService.createMfListaAkciznihDozvola(filters),
    select: (response): MfinExciseLicenceRequestListResponse => ({
      exciseLicenceRequestList: response.data?.akcizneDozvole ?? [],
      productTypes: mapSelectOptions<FilterSelectOption>({
        labelAccessor: "name",
        valueAccessor: "id",
        sourceArray: response.data?.vrsteFilters,
      }),
      requestTypes: mapSelectOptions<FilterSelectOption>({
        labelAccessor: "name",
        valueAccessor: "id",
        sourceArray: response.data?.filterTipResenja,
      }),
      statuses: mapSelectOptions<FilterSelectOption>({
        labelAccessor: "name",
        valueAccessor: "id",
        sourceArray: response.data?.filterStatus,
      }),
    }),
    retry: false,
    enabled,
  });
}

/**
 * Hook used to fetch data for mfin warehouse list
 * @param filters Filters applied to table data
 * @param enabled If true API call is initiated immediately
 * @returns Filter dropdown values that can be applied to data and data after/before data
 */
export function useMfinExciseWarehouseList({
  filters,
  enabled,
}: {
  filters: MfinExciseWarehouseListPayload;
  enabled?: boolean;
}) {
  return useQuery({
    ...mfinKeys.exciseWarehouseList({ filters }),
    queryFn: () => ministryService.postMfListaAkciznihSkladista(filters),
    select: (response): MfinExciseWarehouseListResponse => ({
      warehouseList: response.data?.akciznaSkladista ?? [],
      requestTypes: mapSelectOptions<FilterSelectOption>({
        labelAccessor: "name",
        valueAccessor: "id",
        sourceArray: response.data?.filters,
      }),
    }),
    retry: false,
    enabled,
  });
}

export function useMfinEvidentiraneMarkiceList({
  filters,
}: {
  filters: MfinListaEvidentiranihAkciznihMarkicaPayload;
}) {
  const { setError } = useContext(ModalsContext);
  const sanitizedFilters = sanitizeMfinEvidentiraneMarkiceFilters(filters);

  return useQuery({
    ...mfinKeys.evidentiraneMarkiceList({ filters: sanitizedFilters }),
    queryFn: async () => {
      const [err, response] = await to(
        ministryService.postMfListaEvidentiranihAkciznihMarkica(
          sanitizedFilters
        )
      );
      if (err || !response) throw err;
      if (!response.success && response.errorMessage) {
        setError(response.errorMessage);
        throw new Error(response.errorMessage);
      }
      return response;
    },
    select: (response): MfinListaEvidentiranihAkciznihMarkicaResponse => ({
      evidentiraneMarkice: response.data?.evidentiraneMarkice ?? [],
      kategorijeEvidentiranja: response.data?.kategorijeEvidentiranja ?? [],
      pib: response.data?.pib,
      psNaziv: response.data?.psNaziv,
    }),
    retry: false,
    enabled: Boolean(filters.pib),
  });
}
